import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFnsJalali } from "@mui/x-date-pickers/AdapterDateFnsJalaliV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns-jalali";
import axiosInstance from "../api/axiosConfig";
import BackgroundPic from "./assets/sport.webp";
import Logo from "./assets/logo3.png";
import convertToEnglishDigits from "../utils/convertToEnglishDigits";

const cacheRtl = createCache({
  key: "adapter-date-fns-jalali-demo",
  stylisPlugins: [prefixer, rtlPlugin],
});

function SignUpPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nationalCode: "",
    fName: "",
    lName: "",
    birthday: null, // Jalali date field
    userAddress: "",
    postalCode: "",
    villageName: "",
    phoneSupport: "",
    mobile: "",
    leftHearingPercentage: "",
    rightHearingPercentage: "",
    role: "", // Role selection: "player" or "coach"
    imgFile: null,
    baseSportFieldId: "",
    provinceId: "",
    baseCityId: "",
    hearingStatus: "", // New field for hearing status
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorValidation, setErrorValidation] = useState(false);
  const [success, setSuccess] = useState("");
  const [sportFields, setSportFields] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [provinceLoading, setProvinceLoading] = useState(false);

  useEffect(() => {
    const fetchSportFields = async () => {
      try {
        const response = await axiosInstance.get(
          "https://api.dsfai.ir/api/Base/api/Base/GetBaseSportFieldList",
        );
        if (response.data.isSuccess) {
          setSportFields(response.data.data.dataList);
        }
      } catch (err) {
        if (err.response.data.message) {
          setError(`خطا: ${err.response.data.message}`);
        } else {
          setError("خطا در دریافت لیست تخصص .");
        }
      } finally {
        setLoading(false); // Stop loading
      }
    };

    fetchSportFields();
  }, []);

  useEffect(() => {
    const fetchProvinces = async () => {
      setProvinceLoading(true);
      try {
        const response = await axiosInstance.get(
          "https://api.dsfai.ir/api/Base/api/Base/GetBaseProvinceList",
        );
        if (response.data.isSuccess) {
          setProvinces(response.data.data.dataList);
        }
      } catch (err) {
        if (err.response.data.message) {
          setError(`خطا: ${err.response.data.message}`);
        } else {
          setError("خطا در دریافت لیست استان‌ها.");
        }
      } finally {
        setProvinceLoading(false);
      }
    };

    fetchProvinces();
  }, []);

  const handleProvinceChange = async (e) => {
    const provinceId = e.target.value;
    setFormData({ ...formData, provinceId });
    try {
      const response = await axiosInstance.get(
        `https://api.dsfai.ir/api/Base/api/Base/GetBaseCityList?provinceId=${provinceId}`,
      );
      if (response.data.isSuccess) {
        setCities(response.data.data.dataList);
      }
    } catch (err) {
      if (err.response.data.message) {
        setError(`خطا: ${err.response.data.message}`);
      } else {
        setError("خطا در دریافت لیست شهرها.");
      }
    }
  };

  const validateForm = () => {
    if (
      !formData.nationalCode ||
      !formData.fName ||
      !formData.lName ||
      !formData.birthday ||
      !formData.mobile ||
      !formData.role
    ) {
      return "لطفاً تمام فیلدهای ضروری را پر کنید.";
    }

    // Validate hearing percentage
    if (
      formData.hearingStatus === "hearing" ||
      formData.hearingStatus === "cochlearImplant"
    ) {
      formData.leftHearingPercentage = "0";
      formData.rightHearingPercentage = "0";
    }
    const leftHearing = parseFloat(formData.leftHearingPercentage);
    const rightHearing = parseFloat(formData.rightHearingPercentage);

    if (Number.isNaN(leftHearing) || leftHearing < 0 || leftHearing > 100) {
      return "درصد شنوایی گوش چپ باید بین ۱ و ۱۰۰ باشد.";
    }

    if (Number.isNaN(rightHearing) || rightHearing < 0 || rightHearing > 100) {
      return "درصد شنوایی گوش راست باید بین ۱ و ۱۰۰ باشد.";
    }

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Apply conversion only to specific fields
    if (
      [
        "nationalCode",
        "mobile",
        "rightHearingPercentage",
        "leftHearingPercentage",
        "phoneSupport",
        "postalCode",
      ].includes(name)
    ) {
      newValue = convertToEnglishDigits(value);
    }

    setFormData({
      ...formData,
      [name]: newValue,
    });
  };

  const handleDateChange = (newDate) => {
    setFormData({ ...formData, birthday: newDate });
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, imgFile: e.target.files[0] }));
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setErrorValidation(true);
      return;
    }

    setErrorValidation(false);

    const baseUserTypeId = formData.role === "coach" ? 2 : 1;
    const jalaliDate = format(formData.birthday, "yyyy/MM/dd");

    let leftHearing = formData.leftHearingPercentage || 0;
    let rightHearing = formData.rightHearingPercentage || 0;

    if (formData.hearingStatus === "hearing") {
      leftHearing = 0;
      rightHearing = 0;
    } else if (formData.hearingStatus === "cochlearImplant") {
      leftHearing = 1;
      rightHearing = 1;
    }

    const params = new URLSearchParams({
      NationalCode: formData.nationalCode,
      FName: formData.fName,
      LName: formData.lName,
      LeftHearingPercentage: leftHearing,
      RightHearingPercentage: rightHearing,
      BaseCityId: formData.baseCityId,
      VillageName: formData.villageName,
      UserAddress: formData.userAddress,
      PostalCode: formData.postalCode,
      Mobile: formData.mobile,
      PhoneSupport: formData.phoneSupport,
      Birthday: jalaliDate,
      BaseSportFieldId: formData.baseSportFieldId,
      BaseUserTypeId: baseUserTypeId,
      Status: 0,
    });

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const formDataPayload = new FormData();
      if (formData.imgFile) {
        formDataPayload.append("imgFile", formData.imgFile);
      }

      await axiosInstance.post(
        `/User/api/User/CreateUser?${params.toString()}`,
        formDataPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setSuccess("ثبت نام با موفقیت انجام شد.");
      setTimeout(() => {
        navigate(`/sign-in`);
      }, 2000);
    } catch (err) {
      setError("خطا در ثبت نام. لطفاً دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundImage: `url(${BackgroundPic})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        textAlign: "center",
        color: "#fff",
        padding: "20px",
      }}
    >
      <Box
        sx={{
          backgroundColor: "rgb(255 255 255 / 94%)",
          borderRadius: "10px",
          padding: "30px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.7)",
          width: "90%",
          maxWidth: "600px",
          [theme.breakpoints.down("sm")]: {
            padding: "20px",
            width: "96%",
          },
        }}
      >
        <img
          src={Logo}
          alt="Logo"
          style={{
            width: "300px",
            height: "auto",
            marginBottom: "20px",
          }}
        />

        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          color="textPrimary"
          sx={{
            fontSize: "2rem",
            [theme.breakpoints.down("sm")]: {
              fontSize: "1.5rem",
            },
          }}
        >
          ثبت نام
        </Typography>

        {error && (
          <Typography
            variant="subtitle2"
            color="error"
            sx={{ marginBottom: "10px" }}
          >
            {error}
          </Typography>
        )}

        {success && (
          <Typography
            variant="subtitle2"
            color="success.main"
            sx={{ marginBottom: "10px" }}
          >
            {success}
          </Typography>
        )}

        {loading && <CircularProgress sx={{ mb: 2 }} />}

        <Grid container spacing={3} direction="row-reverse">
          <Grid size={12}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
              color="textPrimary"
            >
              اطلاعات شخصی
            </Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              required
              error={errorValidation}
              placeholder="کد ملی *"
              dir="rtl"
              name="nationalCode"
              value={formData.nationalCode}
              inputProps={{ pattern: "[0-9]*" }}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              error={errorValidation}
              placeholder="نام *"
              dir="rtl"
              name="fName"
              value={formData.fName}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              error={errorValidation}
              placeholder="نام خانوادگی *"
              dir="rtl"
              name="lName"
              value={formData.lName}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CacheProvider value={cacheRtl}>
              <div dir="rtl">
                <LocalizationProvider dateAdapter={AdapterDateFnsJalali}>
                  <DatePicker
                    label="تاریخ تولد *"
                    openTo="year"
                    error={errorValidation}
                    value={formData.birthday}
                    onChange={handleDateChange}
                    sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
            </CacheProvider>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CacheProvider value={cacheRtl}>
              <div dir="rtl">
                <FormControl fullWidth>
                  <InputLabel id="hearing-status-label">
                    وضعیت شنوایی *
                  </InputLabel>
                  <Select
                    error={errorValidation}
                    labelId="hearing-status-label"
                    id="hearingStatus"
                    name="hearingStatus"
                    value={formData.hearingStatus}
                    onChange={handleChange}
                    label="*وضعیت شنوایی"
                    sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
                  >
                    <MenuItem value="deaf" dir="rtl">
                      ناشنوا
                    </MenuItem>
                    <MenuItem value="hearing" dir="rtl">
                      شنوا
                    </MenuItem>
                    <MenuItem value="cochlearImplant" dir="rtl">
                      کاشت حلزون انجام شده
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </CacheProvider>
          </Grid>
          {formData.hearingStatus === "deaf" && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  error={errorValidation}
                  placeholder="درصد ناشنوایی گوش راست *"
                  dir="rtl"
                  name="rightHearingPercentage"
                  value={formData.rightHearingPercentage}
                  inputProps={{ min: 0, max: 100, pattern: "[0-9]*" }}
                  onChange={handleChange}
                  fullWidth
                  sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  error={errorValidation}
                  placeholder="درصد ناشنوایی گوش چپ *"
                  dir="rtl"
                  name="leftHearingPercentage"
                  value={formData.leftHearingPercentage}
                  inputProps={{ min: 0, max: 100, pattern: "[0-9]*" }}
                  onChange={handleChange}
                  fullWidth
                  sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
                />
              </Grid>
              <Grid size={12} textAlign="right">
                <Typography variant="h8" color="info">
                  راهنما: درصد ناشنوایی خود را به صورت عددی وارد کنید. به عنوان
                  مثال، اگر ۳۰ درصد شنوایی دارید، عدد ٧۰ را وارد کنید
                </Typography>
              </Grid>
            </>
          )}
          {/* File Upload */}
          <Grid size={12}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
              color="textPrimary"
            >
              انتخاب عکس پرسنلی
            </Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              dir="rtl"
              // label="انتخاب عکس"
              type="file"
              sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
              inputProps={{ accept: "image/*" }}
              onChange={handleFileChange}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
              color="textPrimary"
            >
              اطلاعات تماس
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              error={errorValidation}
              placeholder="شماره موبایل *"
              dir="rtl"
              name="mobile"
              value={formData.mobile}
              inputProps={{ pattern: "[0-9]*" }}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              placeholder="شماره تلفن ثابت"
              dir="rtl"
              name="phoneSupport"
              value={formData.phoneSupport}
              inputProps={{ pattern: "[0-9]*" }}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
            />
          </Grid>
          <Grid size={12}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
              color="textPrimary"
            >
              آدرس
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CacheProvider value={cacheRtl}>
              <div dir="rtl">
                <FormControl fullWidth>
                  <InputLabel id="province-label">استان *</InputLabel>
                  <Select
                    error={errorValidation}
                    labelId="province-label"
                    id="province"
                    name="provinceId"
                    value={formData.provinceId}
                    onChange={handleProvinceChange}
                    label="*استان"
                    sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
                  >
                    {provinces.map((province) => (
                      <MenuItem key={province.id} value={province.id} dir="rtl">
                        {province.title}
                      </MenuItem>
                    ))}
                  </Select>
                  {provinceLoading && <CircularProgress size={24} />}
                </FormControl>
              </div>
            </CacheProvider>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CacheProvider value={cacheRtl}>
              <div dir="rtl">
                <FormControl fullWidth>
                  <InputLabel id="city-label">شهر *</InputLabel>
                  <Select
                    error={errorValidation}
                    labelId="city-label"
                    id="city"
                    name="baseCityId"
                    value={formData.baseCityId}
                    onChange={handleChange}
                    label="*شهر"
                    sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
                  >
                    {cities.map((city) => (
                      <MenuItem key={city.id} value={city.id} dir="rtl">
                        {city.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </CacheProvider>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              placeholder="آدرس"
              dir="rtl"
              name="userAddress"
              value={formData.userAddress}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              placeholder="کد پستی"
              dir="rtl"
              name="postalCode"
              value={formData.postalCode}
              inputProps={{ pattern: "[0-9]*" }}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              placeholder="نام روستا"
              dir="rtl"
              name="villageName"
              value={formData.villageName}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
            />
          </Grid>
          <Grid size={12}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
              color="textPrimary"
            >
              انتخاب نقش و رشته ورزشی
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CacheProvider value={cacheRtl}>
              <div dir="rtl">
                <FormControl fullWidth>
                  <InputLabel id="role-label">نقش *</InputLabel>
                  <Select
                    error={errorValidation}
                    labelId="role-label"
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    label="*نقش"
                    sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
                  >
                    <MenuItem value="player" dir="rtl">
                      بازیکن
                    </MenuItem>
                    <MenuItem value="coach" dir="rtl">
                      مربی
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </CacheProvider>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CacheProvider value={cacheRtl}>
              <div dir="rtl">
                <FormControl fullWidth>
                  <InputLabel id="sport-field-label">رشته ورزشی *</InputLabel>
                  <Select
                    error={errorValidation}
                    labelId="sport-field-label"
                    id="baseSportFieldId"
                    name="baseSportFieldId"
                    value={formData.baseSportFieldId}
                    onChange={handleChange}
                    label="*رشته ورزشی"
                    sx={{ backgroundColor: "#f9f9f9", borderRadius: "5px" }}
                  >
                    {sportFields.map((field) => (
                      <MenuItem key={field.id} value={field.id} dir="rtl">
                        {field.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </CacheProvider>
          </Grid>
          <Grid size={12} sx={{ textAlign: "center" }}>
            {error && (
              <Typography
                variant="subtitle2"
                color="error"
                sx={{ marginBottom: "10px" }}
              >
                {error}
              </Typography>
            )}

            {success && (
              <Typography
                variant="subtitle2"
                color="success.main"
                sx={{ marginBottom: "10px" }}
              >
                {success}
              </Typography>
            )}
            {loading && <CircularProgress sx={{ mb: 2 }} />}
          </Grid>
          <Grid size={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              sx={{
                padding: "10px 20px",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              ثبت نام
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default SignUpPage;
